import { makeAutoObservable, runInAction } from 'mobx';
import {IChangeable, ISaveable, WithOld} from '../../../stores/types';
import { TYPE, rootContainer } from '../../../services/container/root';
import NotificationToSlotsStore from './notification-to-slots-store';
import { INotificationTemplate, INotificationToSlots, SLOT_TYPE } from '../emailContentTypes';
import EmailContentService from '../../../services/api/email-content-service';
import {
	resetTranslationEditSession,
	trackTranslationEditStarted,
	trackTranslationSaveCompleted,
	TranslationSurface,
} from '../../../translation/metrics/translations-mixpanel';

interface INotificationTemplateChanges extends Partial<INotificationTemplate> {}

class NotificationTemplateStore implements ISaveable, IChangeable<INotificationTemplateChanges> {
	private _changes: INotificationTemplateChanges = {};
	private _model: INotificationTemplate = {} as INotificationTemplate;
	private _isLoading: boolean = true;
	private _isSaving: boolean = false;
	private _eventId: string | undefined;
	private _scope: string;
	/** Sub-stores */
	private _notificationToSlotsStores?: NotificationToSlotsStore[];
	private _selelctedLocale: string;

	constructor(model: INotificationTemplate | undefined, eventId: string, scope: string, selectedLocale: string) {
		this._eventId = eventId;
		this._scope = scope;
		this._selelctedLocale = selectedLocale;
		this._setModel(model);
		makeAutoObservable(this);
	}

	public get scope() {
		return this._scope;
	}

	public get id() {
		return this._changes.id ?? this._model.id;
	}
	public get user_id() {
		return this._changes?.user_id ?? this._model.user_id;
	}
	public get name() {
		return this._changes?.name ?? this._model.name;
	}

	public getHeader(locale: string) {
		return this._getContentByType(locale, SLOT_TYPE.HEADER);
	}

	public getFooter(locale: string) {
		return this._getContentByType(locale, SLOT_TYPE.FOOTER);
	}

	public getContent(locale: string) {
		return this._getContentByType(locale, SLOT_TYPE.CONTENT);
	}

	public getSubject(locale: string) {
		return this._getContentByType(locale, SLOT_TYPE.SUBJECT);
	}

	public setSubject(locale: string, value: string | null) {
		this._setContentByType(locale, value, SLOT_TYPE.SUBJECT);
	}

	public setContent(locale: string, value: string) {
		this._setContentByType(locale, value, SLOT_TYPE.CONTENT);
	}

	public setFooter(locale: string, value: string) {
		this._setContentByType(locale, value, SLOT_TYPE.FOOTER);
	}

	public setSelectedLocale(selectedLocale: string) {
		runInAction(() => (this._selelctedLocale = selectedLocale));
	}

	setEvent(eventId: string | undefined) {
		this._eventId = eventId;
	}

	private _getContentByType(locale: string, slot_name: SLOT_TYPE) {
		let store = this._notificationToSlotsStores?.find((n2s) => n2s.slot_name === slot_name);

		const slot = store?.slot;

		let template = slot?.templates?.find((t) => t.locale === locale);

		return template?.content;
	}

	public init(
		locale: string,
		slotsTypes: SLOT_TYPE[] = [SLOT_TYPE.SUBJECT, SLOT_TYPE.CONTENT, SLOT_TYPE.HEADER, SLOT_TYPE.FOOTER]
	) {

		slotsTypes.forEach((slotName, i) => {
			let store = this._notificationToSlotsStores?.find((n2s) => n2s.slot_name === slotName);

			if (!store) {
				store = this.addNotificationToSlotsStore(store, slotName);
			}

			const slot = store?.slot;

			let template = slot?.templates?.find((t) => t.locale === locale);

			if (!template) {
				template = store?.addTemplate(this._getHandleBarContent(slotName), locale);
			}

		});
	}

	private addNotificationToSlotsStore(store: NotificationToSlotsStore | undefined, slot_name: SLOT_TYPE) {
		return runInAction(() => {
			const changes = {
				slot_name: slot_name,
				slot: {
					name: `${this._eventId}-${slot_name}`,
					templates: [],
				},
			};
			store = new NotificationToSlotsStore();
			store.setChanges(changes);
			this._notificationToSlotsStores?.push(store);

			return store;
		});
	}

	private _getHandleBarContent(slot_name: SLOT_TYPE): string | null {
		switch (slot_name) {
			case SLOT_TYPE.SUBJECT:
				return null;
			case SLOT_TYPE.HEADER:
				return this._getHeaderContentHandlebar();
			case SLOT_TYPE.CONTENT:
				return '';
			case SLOT_TYPE.FOOTER:
				return '';
		}
	}

	private _setContentByType(locale: string, value: string | null, slot_name?: SLOT_TYPE) {
		// Capture clean/dirty before mutation; track only on the clean → dirty transition.
		const hadChangesBeforeEdit = this.hasChanges();
		let store = this._notificationToSlotsStores?.find((n2s) => n2s.slot_name === slot_name);

		store?.setContent(locale, value);
		if (!hadChangesBeforeEdit && this.hasChanges()) {
			trackTranslationEditStarted({
				scope: this._scope,
				locale,
				is_default_locale: false,
				surface: TranslationSurface.Emails,
			});
		}
	}

	discardChanges(): void {
		this._changes = {};
		this._notificationToSlotsStores?.forEach((slot) => slot.discardChanges());
		resetTranslationEditSession(this._scope);
	}

	getUpdatedModel(): INotificationTemplate {
		const hasLocalChanges = !!Object.keys(this._changes).length;

		return {
			...this._model,
			...this._changes,
			slots: this._notificationToSlotsStores?.map<INotificationToSlots>((n2s) => n2s.getUpdatedModel()),
			_has_changes: hasLocalChanges,
		};
	}

	private _setModel(model: INotificationTemplate | undefined): void {
		this._model = model as INotificationTemplate;

		if (model) {
			this._changes = {};
		} else {
			this._changes = { name: this._eventId };
		}

		this._notificationToSlotsStores = this._model?.slots?.map((slot) => new NotificationToSlotsStore(slot));

		if (!this._notificationToSlotsStores) {
			this._notificationToSlotsStores = [];
		}
	}

	private _getHeaderContentHandlebar(): string {
		return `<table align="center" width="100%" cellpadding="0" cellspacing="0" role="presentation" dir={{{ text_dir }}}>
		<tr>
			<td align="{{{ logo_align }}}" style="padding: 30px 0;">
				{{#if logo}}
					<img align="center" alt="" src="{{{ logo }}}" style="max-width: 570px; max-height: 90px; display: inline !important; vertical-align: bottom;" class="mcnRetinaImage">
				{{else}}
					<h3 style="text-align: {{{ logo_align}}}; font-size: 24px; text-transform: uppercase; color: {{{text_color }}}; display: block;">{{ store_name }}</h3>
				{{/if}}
			</td>
		</tr>
	</table>`;
	}

	public get isLoading(): boolean {
		return this._isLoading;
	}

	public get model(): INotificationTemplate {
		return this._model;
	}

	async save() {
        this._isSaving = true;
		const updatedModel: WithOld<INotificationTemplate> = this.getUpdatedModel();
		let savedNotificationTemplate: INotificationTemplate | undefined = undefined;
		const startedAt = Date.now();
		const locale = this._selelctedLocale;
		try {
			if (updatedModel.id) {
                updatedModel.__old = this._model
				savedNotificationTemplate = await this.emailContentService.updateNotification(
					this._eventId,
					updatedModel.id,
					updatedModel
				);
			} else {
				savedNotificationTemplate = await this.emailContentService.createNotification(
					this._eventId,
					updatedModel
				);
			}
			trackTranslationSaveCompleted({
				scope: this._scope,
				locale,
				is_default_locale: false,
				result: "success",
				fields_changed_count: 1,
				locales_changed_count: 1,
				missing_before: 0,
				missing_after: 0,
				duration_ms: Date.now() - startedAt,
				surface: TranslationSurface.Emails,
			});
		} catch (e) {
			console.error(`EmailTemplateStore.save: ${e}`);
			this._isSaving = false;
			trackTranslationSaveCompleted({
				scope: this._scope,
				locale,
				is_default_locale: false,
				result: "error",
				fields_changed_count: 1,
				locales_changed_count: 1,
				missing_before: 0,
				missing_after: 0,
				duration_ms: Date.now() - startedAt,
				surface: TranslationSurface.Emails,
			});
		}

		runInAction(() => {
			if (savedNotificationTemplate) {
				this._setModel(savedNotificationTemplate);
			}
			this._isSaving = false;
			return savedNotificationTemplate;
		});
	}

	private get emailContentService(): EmailContentService {
		return rootContainer.get<EmailContentService>(TYPE.EmailContentService);
	}

	public hasChanges(): boolean {
		const slotsChanges = this._notificationToSlotsStores?.map((slot) => slot.hasChanges());

		return (
			!!Object.keys(this._changes).length ||
			!!slotsChanges?.reduce((accumulator, currentValue) => accumulator || currentValue, false)
		);
	}

	isSaving(): boolean {
		return this._isSaving;
	}
}

export default NotificationTemplateStore;
